/**
 * /privacy — privacy policy in plain language. Goal: tell users what
 * we collect, what we do with it, what we don't do. Not a legal
 * document — that's elsewhere. This is the human-readable summary.
 */
import { MarketingShell } from '../MarketingShell';

export function Privacy() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/privacy"
      documentTitle="Privacy at Draftlize · Draftlize"
      documentDescription="What we collect, what we do with it, what we don't do. Plain language. No model training on your data. Cookies only for auth. Delete on request within 7 days."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Privacy</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Privacy <em>in plain language.</em>
          </h1>
          <p className="lede">
            The legal version of this is the formal Terms / Privacy Policy at the bottom of every page. This is the human version: what we actually collect, what we actually do with it, and what we don't do.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">What we <em>collect.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Your cards, threads, refs</h3>
            <p>The actual content you create. Stored in your tenant-scoped Postgres + S3 bucket. Visible only to you and to LLM upstream during your calls.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Account basics</h3>
            <p>Email, OAuth provider ID (GitHub / Google), display name. That's it. We don't ask for your job title, company size, anything else.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Usage events for billing</h3>
            <p>Token-cost-cents per LLM call, timestamped, for the 5-hour rolling quota window. Aggregated only — not "what you said," just "you used 47 cents in this window."</p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">Error logs (anonymized)</h3>
            <p>If your browser hits an error, we log the stack trace + your user_id + a request ID for triage. Stored in our own DB. No PII content (your card body isn't in the log).</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">What we <em>don't</em> collect.</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">×</span>
            <h3 className="serif">Marketing analytics</h3>
            <p>No Google Analytics. No Mixpanel. No third-party tracking pixel. Marketing pages have zero third-party scripts.</p>
          </div>
          <div className="problem-col">
            <span className="num">×</span>
            <h3 className="serif">Cross-site tracking</h3>
            <p>No third-party cookies. The only cookie we set is the session cookie (auth) and the i18n locale preference.</p>
          </div>
          <div className="problem-col">
            <span className="num">×</span>
            <h3 className="serif">Training data</h3>
            <p>We do not train models on your data. The LLM providers we call (GLM, DeepSeek) are on no-training contracts. We don't pipe your cards into our own model training pipeline — we don't have one.</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Your <em>rights.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Export anytime</h3>
            <p>Settings → Export. Full markdown dump of every card, thread, document, history, ref. Plain files. No proprietary format. No friction.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Delete on request</h3>
            <p>Email <a href="mailto:hi@draftlize.com">hi@draftlize.com</a>. We confirm by replying, you confirm, we delete. Within 7 calendar days. Includes backups (next backup cycle wipes the affected rows). We send you a confirmation when it's done.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">GDPR / CCPA requests</h3>
            <p>Same email. We'll fulfill access / portability / deletion requests within the 30-day window required by both regulations.</p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">Leave anytime</h3>
            <p>No subscription to cancel — just stop topping up. Your data stays exportable, and you can delete your account whenever you want.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Default assumption: your cards belong to you. We're a platform you pay so the substrate works — not an adtech company trying to monetize what you said.
        </blockquote>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Privacy questions? <em>Real humans answer.</em></h2>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            <a href="mailto:hi@draftlize.com">hi@draftlize.com</a> reaches us. Real reply within 48h on weekdays. Faster for export / delete / GDPR requests.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
